import { DAY } from '../constants/expiry'
import type { SecurityOptions } from '../types/share'

export type Tier = 'free' | 'starter' | 'pro'

// ---------------------------------------------------------------------------
// Tier limits (source of truth for both API and UI)
// ---------------------------------------------------------------------------

export interface TierLimits {
  maxActiveShares: number | null  // null = unlimited
  maxStorageBytes: number
  maxFileSizeBytes: number
  maxExpirationMs: number | null  // null = no expiration allowed (never)
}

/**
 * Hard limit for single file upload (HTTP transfer limit)
 * Well under Cloudflare's 100 MB limit for reliability and UX
 */
export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024 // 25 MB

export const TIER_LIMITS: Record<Tier, TierLimits> = {
  free: {
    maxActiveShares: 20,
    maxStorageBytes: 1 * 1024 * 1024,             // 1 MB
    maxFileSizeBytes: MAX_FILE_SIZE_BYTES,        // 25 MB (but limited by storage)
    maxExpirationMs: 7 * DAY,                       // 7 days
  },
  starter: {
    maxActiveShares: 100,
    maxStorageBytes: 25 * 1024 * 1024,            // 25 MB
    maxFileSizeBytes: MAX_FILE_SIZE_BYTES,        // 25 MB
    maxExpirationMs: 30 * DAY,                      // 30 days
  },
  pro: {
    maxActiveShares: null,                        // Unlimited
    maxStorageBytes: 100 * 1024 * 1024,           // 100 MB
    maxFileSizeBytes: MAX_FILE_SIZE_BYTES,        // 25 MB
    maxExpirationMs: null,                        // No expiration (never) allowed
  }
} as const

export const DEFAULT_TIER: Tier = 'free'

// ---------------------------------------------------------------------------
// Rate limits
// ---------------------------------------------------------------------------

export interface RateLimits {
  requestsPerMinute: number
  requestsPerDay: number
}

export const RATE_LIMITS: Record<Tier, RateLimits> = {
  free: {
    requestsPerMinute: 30,
    requestsPerDay: 1000,
  },
  starter: {
    requestsPerMinute: 60,
    requestsPerDay: 5000,
  },
  pro: {
    requestsPerMinute: 120,
    requestsPerDay: 15000,
  }
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

export const TIER_PRICING: Record<Tier, { monthly: number; label: string }> = {
  free: { monthly: 0, label: 'Free' },
  starter: { monthly: 3, label: '$3/mo' },
  pro: { monthly: 8, label: '$8/mo' },
}

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

export interface TierFeatures {
  noCaptcha: boolean
  customShortUrls: boolean
  viewAnalytics: boolean
  customBranding: boolean
  editableShares: boolean
  embedAccess: boolean
}

export const TIER_FEATURES: Record<Tier, TierFeatures> = {
  free: {
    noCaptcha: false,
    customShortUrls: false,
    viewAnalytics: false,
    customBranding: false,
    editableShares: false,
    embedAccess: false,
  },
  starter: {
    noCaptcha: true,
    customShortUrls: false,
    viewAnalytics: false,
    customBranding: false,
    editableShares: false,
    embedAccess: false,
  },
  pro: {
    noCaptcha: true,
    customShortUrls: true,
    viewAnalytics: true,
    customBranding: true,
    editableShares: true,
    embedAccess: true,
  },
}

/**
 * Human-readable limit names for error messages
 */
export const LIMIT_NAMES: Record<string, string> = {
  share_count: 'active shares',
  storage: 'total storage',
  file_size: 'file size',
  expiration: 'expiration time',
  rate_minute: 'requests per minute',
  rate_day: 'requests per day',
}

// ---------------------------------------------------------------------------
// Tier permission logic
// ---------------------------------------------------------------------------

/**
 * Expiry options with tier restrictions.
 * Each option specifies the minimum tier required (if any).
 */
export const TIER_EXPIRY_OPTIONS = [
  { value: '1h', label: '1 hour' },
  { value: '12h', label: '12 hours' },
  { value: '1d', label: '1 day' },
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days', minTier: 'starter' as const },
  { value: '90d', label: '90 days', minTier: 'pro' as const },
  { value: '180d', label: '180 days', minTier: 'pro' as const },
  { value: '1y', label: '1 year', minTier: 'pro' as const },
  { value: 'never', label: 'Never', minTier: 'pro' as const },
] as const

/** Maximum expiry option per tier */
const TIER_MAX_EXPIRY: Record<Tier, string> = {
  free: '7d',
  starter: '30d',
  pro: 'never',
}

const TIER_ORDER: Record<string, number> = { free: 0, starter: 1, pro: 2 }

/**
 * Check if a tier meets the minimum tier requirement.
 */
export function tierMeetsMin(tier: string, minTier?: 'starter' | 'pro'): boolean {
  if (!minTier) return true
  return (TIER_ORDER[tier] ?? 0) >= (TIER_ORDER[minTier] ?? 0)
}

/**
 * Check whether a specific expiry option is allowed for the given tier.
 */
export function isExpiryOptionAllowed(expiryValue: string, tier: Tier): boolean {
  const option = TIER_EXPIRY_OPTIONS.find(o => o.value === expiryValue)
  if (!option) return false
  return !('minTier' in option) || tierMeetsMin(tier, option.minTier)
}

/**
 * Clamp the security options to the tier's limits.
 * Returns null if no changes needed, or the corrected options.
 */
export function enforceSecurityTierRules(options: SecurityOptions, tier: Tier): SecurityOptions | null {
  let changed = false
  let result = { ...options }

  // Clamp expiry to tier max
  const currentOption = TIER_EXPIRY_OPTIONS.find(o => o.value === options.expiresIn)
  if (currentOption && 'minTier' in currentOption && !tierMeetsMin(tier, currentOption.minTier)) {
    result = { ...result, expiresIn: TIER_MAX_EXPIRY[tier] }
    changed = true
  }

  // Reset isModifiable if not Pro
  if (tier !== 'pro' && options.isModifiable) {
    result = { ...result, isModifiable: false }
    changed = true
  }

  return changed ? result : null
}

/**
 * Determine whether the burn-after-read option should be disabled.
 */
export function isBurnDisabled(isPublic: boolean, isModifiable: boolean): boolean {
  return isPublic || isModifiable
}

/**
 * Determine whether the modifiable option should be disabled.
 */
export function isModifiableDisabled(tier: Tier, isPublic: boolean, burnAfterRead: boolean): boolean {
  return tier !== 'pro' || isPublic || burnAfterRead
}

/**
 * Get the tooltip message for the burn-after-read toggle.
 */
export function getBurnTooltip(isPublic: boolean, isModifiable: boolean): string {
  if (isPublic) return 'Burn after read is not available for public shares'
  if (isModifiable) return 'Burn after read is not available for modifiable shares'
  return ''
}

/**
 * Get the tooltip message for the modifiable toggle.
 */
export function getModifiableTooltip(tier: Tier, isPublic: boolean, burnAfterRead: boolean): string {
  if (tier !== 'pro') return 'Modifiable shares require Pro tier'
  if (isPublic) return 'Modifiable is not available for public shares'
  if (burnAfterRead) return 'Modifiable is not available for burn-after-read shares'
  return ''
}

/**
 * Get the tier's maximum expiry label for display.
 */
export function getTierMaxExpiryLabel(tier: Tier): string | null {
  if (tier === 'free') return 'Max 7 days on free tier'
  if (tier === 'starter') return 'Max 30 days on Starter tier'
  return null
}

/**
 * Calculate usage percentage, clamped to 0-100.
 */
export function calculateUsagePercentage(current: number, max: number): number {
  if (max < 0) return 0 // unlimited
  return Math.min((current / max) * 100, 100)
}

/**
 * Determine usage status based on percentage.
 */
export function getUsageStatus(current: number, max: number): 'unlimited' | 'normal' | 'warning' | 'exceeded' {
  if (max < 0) return 'unlimited'
  const percentage = calculateUsagePercentage(current, max)
  if (percentage >= 100) return 'exceeded'
  if (percentage > 80) return 'warning'
  return 'normal'
}

/**
 * Validate an account name.
 */
export function validateAccountName(
  name: string,
): { valid: true } | { valid: false; error: string } {
  if (!name.trim()) {
    return { valid: false, error: 'Account name is required' }
  }
  if (name.trim().length < 2) {
    return { valid: false, error: 'Account name must be at least 2 characters' }
  }
  return { valid: true }
}

/**
 * Validate API key format (must start with "mk_").
 */
export function validateApiKeyFormat(
  key: string,
): { valid: true } | { valid: false; error: string } {
  const trimmed = key.trim()
  if (!trimmed) {
    return { valid: false, error: 'Secret key is required' }
  }
  if (!trimmed.startsWith('mk_')) {
    return { valid: false, error: 'Invalid key format. Keys start with mk_' }
  }
  return { valid: true }
}

/**
 * Mask a secret key for display: show first 6 and last 4 chars with dots.
 */
export function maskSecretKey(key: string): string {
  if (key.length <= 10) return key
  const prefix = key.slice(0, 6)
  const suffix = key.slice(-4)
  return `${prefix}${'•'.repeat(8)}${suffix}`
}
